'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import type { ListDetail } from '@swishapp/api-client';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  ResponsiveDialog,
  ResponsiveDialogClose,
  ResponsiveDialogContent,
  ResponsiveDialogTrigger,
} from '../../../responsive-dialog';
import { Button } from '../../../ui/button';
import { DialogFooter } from '../../../ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../ui/form';
import { Input } from '../../../ui/input';

const renameBoardSchema = z.object({
  name: z
    .string()
    .min(1, 'Board name is required')
    .max(50, 'Board name must be 50 characters or less')
    .trim(),
});

type RenameBoardFormData = z.infer<typeof renameBoardSchema>;

interface RenameBoardDialogProps {
  board: ListDetail;
  onRename: (newName: string) => void;
  isRenaming?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: ReactNode;
  primaryButtonClassName?: string;
  secondaryButtonClassName?: string;
}

export function RenameBoardDialog({
  board,
  onRename,
  isRenaming = false,
  open: controlledOpen,
  onOpenChange,
  children,
  primaryButtonClassName,
  secondaryButtonClassName,
}: RenameBoardDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const form = useForm<RenameBoardFormData>({
    resolver: zodResolver(renameBoardSchema),
    defaultValues: {
      name: board.name,
    },
  });

  const onSubmit = (data: RenameBoardFormData) => {
    if (data.name.trim() !== board.name) {
      onRename(data.name.trim());
      setOpen(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      form.reset({ name: board.name });
    }
  };

  return (
    <ResponsiveDialog isOpen={open} onOpenChange={handleOpenChange}>
      {children && (
        <ResponsiveDialogTrigger asChild>{children}</ResponsiveDialogTrigger>
      )}
      <ResponsiveDialogContent
        className="max-w-md"
        title="Rename Board"
        description="Enter a new name for your board."
      >
        <div className="pt-2">
          <Form {...form}>
            <form
              onSubmit={(e) => {
                form.handleSubmit(onSubmit)(e);
              }}
              className="space-y-4"
            >
              <div className="px-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(var(--color))]">
                        Board Name
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Enter board name"
                          autoFocus
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter>
                <ResponsiveDialogClose asChild>
                  <Button
                    className={secondaryButtonClassName}
                    type="button"
                    disabled={isRenaming}
                  >
                    Cancel
                  </Button>
                </ResponsiveDialogClose>
                <Button
                  className={primaryButtonClassName}
                  type="submit"
                  disabled={
                    !form.formState.isValid ||
                    form.formState.isSubmitting ||
                    isRenaming
                  }
                >
                  {isRenaming ? 'Renaming...' : 'Rename Board'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </div>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
